import React, { useCallback, useEffect, useRef, useState } from 'react';
import integrationsService from '../../services/integrationsService';
import { apiErrorMessage } from '../../utils/apiError';
import ResyncFields, { filledRangesOf, anyBadOrderOf, kindDefOf } from './ResyncFields';
import '../shared/ConfirmModal.css';

// BULK RESYNC — resync MANY pipeline implementations at once with ONE set of criteria, from the Hotels tab's selection
// (PipelineHotelsTab). It picks the kind + date ranges exactly as the single ResyncModal does (ResyncFields, so both
// offer the same searches), then starts one resync run per selected implementation and polls them all — each run is the
// same per-implementation Step Functions machine the single modal starts, so there is no new server behaviour, only many
// at once.
//
// Only implementations whose source can be resynced are passed in (rows already filtered by the caller on `resyncable`).
// A run that cannot start (e.g. its source lost its Polling Implementation) is shown with its reason and does not stop
// the others.

const TERMINAL = ['done', 'failed'];
const POLL_MS = 2500;
const statusDot = (status) => (status === 'done' ? '#16a34a' : status === 'failed' ? '#dc2626' : status === 'running' ? '#2563eb' : '#d1d5db');

// One line describing a run's state, from its OpenSearch document.
const runSummary = (run) => {
    if (!run) return 'starting…';
    if (run.status === 'failed') return `failed${run.error ? ` — ${run.error}` : ''}`;
    const counts = (run.count_new || run.count_updated || run.count_unchanged)
        ? ` · ${run.count_new || 0} new, ${run.count_updated || 0} updated, ${run.count_unchanged || 0} unchanged` : '';
    if (run.status === 'done') return `done — ${run.sent || 0} sent${run.failed ? `, ${run.failed} failed` : ''}${counts}`;
    const step = (run.steps || []).find(s => s.status === 'running');
    const detail = step && (step.detail || step.label);
    return `${detail || 'running…'}${run.sent ? ` — ${run.sent}${run.total ? ` of ${run.total}` : ''} sent${counts}` : ''}`;
};

export default function BulkResyncModal({ rows, onClose }) {
    const [kind, setKind] = useState('reservations');
    const [ranges, setRanges] = useState({});
    const [error, setError] = useState(null);
    const [starting, setStarting] = useState(false);
    const [runs, setRuns] = useState(null);          // null before start; after: [{ implId, templateId, hotelname, runId, run, error }]
    const timer = useRef(null);

    const kindDef = kindDefOf(kind);
    const available = kindDef.available !== false;
    const filledRanges = filledRangesOf(kind, ranges);
    const anyRange = Object.keys(filledRanges).length > 0;
    const anyBadOrder = anyBadOrderOf(kind, ranges);
    const setRange = (rk, end, value) => setRanges(prev => ({ ...prev, [rk]: { ...prev[rk], [end]: value } }));

    const stop = useCallback(() => { if (timer.current) { clearInterval(timer.current); timer.current = null; } }, []);
    useEffect(() => stop, [stop]);

    const poll = useCallback(async (started) => {
        const next = await Promise.all(started.map(async (s) => {
            if (!s.runId || (s.run && TERMINAL.includes(s.run.status))) return s;
            try {
                const res = await integrationsService.getResyncStatus(s.templateId, s.implId, s.runId);
                return { ...s, run: res.run || res };
            } catch (e) {
                return s;   // a transient poll failure keeps the last state; the next tick retries
            }
        }));
        setRuns(next);
        if (next.every(s => !s.runId || (s.run && TERMINAL.includes(s.run.status)))) stop();
    }, [stop]);

    const start = async () => {
        setError(null);
        if (!available) { setError('Block resync is not available yet.'); return; }
        if (!anyRange) { setError('Enter at least one date range (a from and a to).'); return; }
        if (anyBadOrder) { setError('Each range’s From must be on or before its To.'); return; }
        setStarting(true);
        // Start every run; a failure to start one is kept on that row, not thrown.
        const started = await Promise.all(rows.map(async (row) => {
            const base = { implId: row.id, templateId: row.template_id, hotelname: row.hotelname || row.name, template_name: row.template_name };
            try {
                const res = await integrationsService.startResync(row.template_id, row.id, { type: kind, ranges: filledRanges });
                return { ...base, runId: res.runId, run: null, error: null };
            } catch (e) {
                return { ...base, runId: null, run: null, error: apiErrorMessage(e, 'could not be started') };
            }
        }));
        setRuns(started);
        setStarting(false);
        if (started.some(s => s.runId)) {
            await poll(started);
            timer.current = setInterval(() => poll(started), POLL_MS);
        }
    };

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    const startedRuns = runs || [];
    const done = runs && startedRuns.every(s => !s.runId || (s.run && TERMINAL.includes(s.run.status)));
    const startedCount = startedRuns.filter(s => s.runId).length;
    const failedToStart = startedRuns.filter(s => !s.runId).length;

    return (
        <div className="cm-backdrop" onClick={onClose}>
            <div className="cm-dialog cm-dialog-wide" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
                <h2 className="cm-title">Resync {rows.length} {rows.length === 1 ? 'pipeline' : 'pipelines'}</h2>

                {!runs ? (
                    <>
                        <p className="cm-message">
                            Ask each selected source for the same date range of records again and send them through its
                            pipeline — the same criteria for every hotel. Each hotel runs its own resync.
                        </p>

                        <ResyncFields
                            kind={kind}
                            ranges={ranges}
                            onKind={(k) => { setKind(k); setError(null); }}
                            onRange={setRange}
                        />

                        <div className="cm-message" style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.5rem' }}>
                            {rows.length} {rows.length === 1 ? 'hotel' : 'hotels'}: {rows.map(r => r.hotelname || r.name).join(', ')}
                        </div>
                    </>
                ) : (
                    <>
                        <p className="cm-message">
                            {done
                                ? 'The resyncs have finished.'
                                : 'The resyncs are running. You can leave this open to watch them, or close it — they keep running.'}
                            {' '}Started {startedCount} of {startedRuns.length}{failedToStart ? `, ${failedToStart} could not start` : ''}.
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', maxHeight: 320, overflowY: 'auto',
                            margin: '0.5rem 0 0.25rem' }}>
                            {startedRuns.map(s => (
                                <div key={s.implId} style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem',
                                    borderBottom: '1px solid #f1f5f9', padding: '0.35rem 0' }}>
                                    <span style={{ width: 9, height: 9, borderRadius: '50%', flexShrink: 0, alignSelf: 'center',
                                        background: s.error ? '#dc2626' : statusDot(s.run && s.run.status) }} />
                                    <span style={{ fontWeight: 600, color: '#111827', minWidth: 160 }}>{s.hotelname}</span>
                                    <span style={{ fontSize: '0.85rem', color: s.error ? '#dc2626' : '#6b7280' }}>
                                        {s.error ? s.error : runSummary(s.run)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {error && <div className="cm-message" style={{ color: '#dc2626' }}>{error}</div>}

                <div className="cm-actions">
                    <button className="cm-btn-cancel" onClick={onClose}>
                        {runs && !done ? 'Close (keeps running)' : 'Close'}
                    </button>
                    {!runs && (
                        <button className="cm-btn-confirm" onClick={start} disabled={starting || !available || !anyRange}>
                            {starting ? 'Starting…' : `Start ${rows.length} ${rows.length === 1 ? 'resync' : 'resyncs'}`}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
